import { NonEmptyArray } from 'fp-ts/lib/NonEmptyArray';
import * as Pr from './product';
import * as Arr from 'fp-ts/lib/Array';
import { pipe } from 'fp-ts/lib/function';
import {
    FurnitureItem,
    Bundle,
    furnitureProductIds,
    furniturePrice,
    bundleExtraProducts,
} from './furniture';
import * as ProductDataStore from '../store/productData';

export interface Bundling {
    bundles: Array<Bundle>;
    singleItems: Array<FurnitureItem>;
}

// An in-progress attempt to bundle a set of items with a specific template
// bundle.
interface PartialBundling {
    // Items considered but not included in the current bundling
    remainingItems: Array<FurnitureItem>;
    // Items considered and included in the current bundling
    bundledItems: Array<FurnitureItem>;
    // The extra products within bundled items that aren't provided by the original
    // bundle template - these increase the cost of the final customised bundle
    extraProducts: Array<string>;
    // Products remaining in the bundle which have not been used to satisfy any
    // previous bundled items
    unusedBundleProducts: Array<string>;
}

function extendPartialBundling(
    { remainingItems, bundledItems, extraProducts, unusedBundleProducts }: PartialBundling,
    item: FurnitureItem,
): PartialBundling {
    unusedBundleProducts = unusedBundleProducts.slice();
    // A bundle covers an item if it contains any of the products in that item.
    // In future, we may need to consider different choices of item to match against
    // a bundle's products.
    if (furnitureProductIds(item).some(productId => unusedBundleProducts.includes(productId))) {
        // Ensure we don't reuse bundle products for other items
        furnitureProductIds(item).forEach(productId => {
            const productPosition = unusedBundleProducts.indexOf(productId);
            if (productPosition >= 0) {
                unusedBundleProducts.splice(productPosition, 1);
            } else {
                extraProducts = [...extraProducts, productId];
            }
        });
        bundledItems = [...bundledItems, item];
    } else {
        remainingItems = [...remainingItems, item];
    }
    return { remainingItems, bundledItems, extraProducts, unusedBundleProducts };
}

function createPartialBundling(bundle: ProductDataStore.Bundle): PartialBundling {
    return {
        remainingItems: [],
        bundledItems: [],
        extraProducts: [],
        unusedBundleProducts: bundle.productIds.slice(),
    };
}

/**
 * Generate a customised bundle from a partial bundling
 *
 * The resulting bundle is based on the supplied original bundle, with
 * price and items customised for the specific items covered by the bundle.
 * Any items not covered by the bundle at all are returned as remainingItems
 * for further processing.
 */
function finishBundle(
    getProductPrice: (sku: string) => number,
    partialBundling: PartialBundling,
    originalBundle: ProductDataStore.Bundle,
): {
    bundle: Bundle;
    remainingItems: Array<FurnitureItem>;
} | null {
    const { remainingItems, bundledItems, extraProducts, unusedBundleProducts } = partialBundling;

    // We only want to use a bundle if all the products it provides are needed.
    // Although we do a preliminary check for this before considering a bundle,
    // that check only ensures that each product within the bundle is somewhere
    // in the remaining unbundled products. The preliminary check can pass while
    // being unable to actually use a bundle - notably if a bundle contains
    // duplicates of any product, a single instance of that product in the items
    // to bundle will cause the earlier test to pass while being unable to actually
    // find a valid bundling due to the extra products. This final check therefore
    // ensures we've managed to use every product in the current bundle.
    if (unusedBundleProducts.length > 0) {
        return null;
    }

    // Construct the basket bundle with any extra products included
    const bundle = {
        canonicalId: originalBundle.canonicalId,
        price:
            originalBundle.price +
            extraProducts.reduce((extraPrice, product) => extraPrice + getProductPrice(product), 0),
        items: bundledItems,
        originalProductIds: originalBundle.productIds,
    };

    return { bundle, remainingItems };
}

/**
 * Generate all valid bundlings for the supplied furnitures
 */
function calculateBundlings(
    getProductPrice: (sku: string) => number,
    bundles: Array<ProductDataStore.Bundle>,
    items: Array<Array<FurnitureItem>>,
): NonEmptyArray<Bundling> {
    return [
        // Always generate the "no bundles" solution
        {
            bundles: [],
            singleItems: items.flat(),
        },
        ...bundles.flatMap((bundle, i) => {
            // Only consider this bundle if all its products are included
            if (
                bundle.productIds.length > 0 &&
                bundle.productIds.every(bundleProduct =>
                    items.flatMap(([item]) => furnitureProductIds(item)).includes(bundleProduct),
                )
            ) {
                // Entirely exclude items that don't contain any products provided
                // by the bundle - we never need to consider these in generating
                // reasonable candidate bundlings.
                const { left: unusableItems, right: candidateItems } = pipe(
                    items,
                    Arr.partition(([item]) =>
                        item.products.some(p => bundle.productIds.includes(Pr.productId(p))),
                    ),
                );
                // In the presence of multiple items that can partially satisfy bundles
                // there may be a choice of which of those items to put into the bundle.
                // Consider all choices of nonempty subsets of items to put into the bundle
                // to ensure we eventually consider every reasonable candidate bundling
                const itemSubsets = candidateItems.reduce(
                    (itemSubsets, items) =>
                        new Array(items.length + 1).fill(null).flatMap((_, i) =>
                            itemSubsets.flatMap(({ included, excluded }) => [
                                {
                                    included: [...included, ...items.slice(0, i)],
                                    excluded:
                                        i < items.length ? [...excluded, items.slice(i)] : excluded,
                                },
                            ]),
                        ),
                    [{ included: [], excluded: [] }] as Array<{
                        included: Array<FurnitureItem>;
                        excluded: Array<Array<FurnitureItem>>;
                    }>,
                );

                return itemSubsets.flatMap(itemSubset => {
                    const partialBundling = itemSubset.included.reduce(
                        extendPartialBundling,
                        createPartialBundling(bundle),
                    );

                    const bundling = finishBundle(getProductPrice, partialBundling, bundle);

                    // Skip this bundle if we didn't actually use it
                    if (!bundling) {
                        return [];
                    }

                    // Or if we didn't use all the items we wanted to.
                    if (bundling.remainingItems.length > 0) {
                        return [];
                    }

                    const remainingBundles = bundles.slice(i);
                    const remainingItems = [...itemSubset.excluded, ...unusableItems];

                    // Recurse with any remaining bundles we may be able to use, and all items not
                    // part of any bundle
                    // Remaining items will consist of items we started with, excluding any items used
                    // in the bundle. This will be strictly greater than zero items as otherwise we
                    // would have rejected the bundle entirely.
                    // Remaining bundles will at most consist of all bundles we started with, but will
                    // exclude any bundles we've already considered without using.
                    // The result of this is that each subproblem we recurse upon is guaranteed to be
                    // strictly smaller/easier than the original bundling problem, so we are guaranteed
                    // to make progress at each step.
                    return calculateBundlings(
                        getProductPrice,
                        remainingBundles,
                        remainingItems,
                    ).map(({ bundles, singleItems }) => ({
                        bundles: [bundling.bundle, ...bundles],
                        singleItems,
                    }));
                });
            } else {
                return [];
            }
        }),
    ];
}

function calculatePrice(bundling: Bundling): number {
    return bundling.bundles
        .map(bundle => bundle.price)
        .concat(bundling.singleItems.map(entry => furniturePrice(entry)))
        .reduce<number>((a, p) => a + p, 0);
}

function basketExtraProducts(basket: Bundling): number {
    return basket.bundles
        .map(b => bundleExtraProducts(b).length)
        .reduce((current, each) => current + each, 0);
}

function compareBundlings(
    a: { price: number; basket: Bundling },
    b: { price: number; basket: Bundling },
): number {
    const extraProductsA =
        basketExtraProducts(a.basket) +
        a.basket.singleItems.reduce((total, item) => total + furnitureProductIds(item).length, 0);
    const extraProductsB =
        basketExtraProducts(b.basket) +
        b.basket.singleItems.reduce((total, item) => total + furnitureProductIds(item).length, 0);
    return a.price < b.price || (a.price == b.price && extraProductsA < extraProductsB) ? -1 : 1;
}

/**
 * Calculate the lowest cost bundling of a list of furniture items
 *
 * Valid bundlings currently must not split a furniture item containing
 * multiple products across more than one bundle. A single bundle
 * produced by the resulting bundling may contain extra products than
 * the original bundle - inspecting the products in the individual items
 * is necessary to obtain a complete list. Similarly, a bundle price may
 * be altered from the original price to account for any extra products
 * in items partially satisfied by that bundle.
 */
export function resolveItemBundles(
    getProductPrice: (sku: string) => number,
    // All available bundles to try to resolve to
    bundles: Array<ProductDataStore.Bundle>,
    // All furniture items to resolve into the lowest-cost basket bundling
    items: Array<FurnitureItem>,
): Bundling {
    const groupedItems = Object.values(
        items.reduce((items, item) => {
            const dedupKey = furnitureProductIds(item).sort().join('###');
            return { ...items, [dedupKey]: [...(items[dedupKey] ?? []), item] };
        }, {} as Record<string, Array<FurnitureItem>>),
    );

    const [firstCandidate, ...restCandidates] = calculateBundlings(
        getProductPrice,
        bundles,
        groupedItems,
    ).map(basket => ({
        basket,
        price: calculatePrice(basket),
    }));
    return restCandidates.reduce(
        (min, option) => (compareBundlings(option, min) < 0 ? option : min),
        firstCandidate,
    ).basket;
}

/** A list of Bundle IDs within a Bundling */
export function bundleIds(b: Bundling) {
    return b.bundles.map(bundle => bundle.canonicalId);
}

/** A list of single Product IDs within a Bundling */
export function singleProductIds(b: Bundling) {
    return b.singleItems.flatMap(item => item.products.map(Pr.productId));
}
